import type { LeaseList, User, UserList } from '@/api/types'
import { statusLabel } from '@/domain/node'
import { ownerLabel } from '@/domain/owner'
import { clusterStatus, match, matchAny, membersOf, opensDetail, personRow, teamsOf } from './model'
import type { World } from './model'

// 상단 바의 검색 결과. 섹션은 화면이고 순서는 사용자 · 팀 · VM · 클러스터 · 노드다.
// **사람과 VM 은 서버가 찾은 줄이다** — 사용자 목록·VM 목록과 같은 찾기(`GET /users`·`GET /leases` 의 `q`)를 부르는 쪽
// (`TopSearch.tsx`)이 묻고, 여기서는 받은 줄을 거르지 않는다. 무엇으로 걸리는지는 스펙이 원본이다.
// 팀·클러스터·노드는 관리자 껍데기가 이미 읽어 둔 작은 목록(`useWorld`)에서 이름으로 찾는다(팀은 태그도).
// 관리자에게는 클러스터·노드 목록이 없어 그 섹션도 서지 않는다.
// 섹션 안에서는 이름이 친 글자로 시작하는 것이 앞이고, 나머지는 받은 순서 그대로다 — 서버가 찾은 섹션은 받은 줄 안에서다.
// **줄에 보이지 않는 값으로 걸린 사람은 그 값을 보조 줄에 보인다** — 이메일로만 걸린 사람은 이메일이 선다.
// 사람은 그 줄이 왜 섰는지 알아야 한다. VM 은 소유자의 아이디·태그·GPU·이미지로도 걸리고 보조 줄은 소유자와 노드뿐이다 —
// VM 목록의 검색 칸과 같은 찾기라 받아들였다.

export type HitKind = 'user' | 'team' | 'vm' | 'cluster' | 'node'

export type Hit = {
  key: string
  label: string
  /** 같은 이름끼리 가려 주는 보조 한 줄. */
  detail: string
  /** 가는 곳. null 이면 열 수 없는 줄이다 — 관리자에게 자기 팀 밖의 사람(`opensDetail`). */
  to: string | null
  /** 간 화면을 좁힐 찾기. 상세가 없는 클러스터는 클러스터 화면을 그 이름으로 좁힌다. 없으면 찾기를 비운다. */
  query?: string
}

/** `total` 은 서버가 찾은 섹션의 맞은 수다 — 받은 줄보다 많으면 머리가 `받은/맞은` 이다. */
export type HitGroup = { kind: HitKind; title: string; hits: Hit[]; total?: number | undefined }

/** 서버가 찾은 것 — 친 말 하나에 대한 계정과 임대의 한 쪽. */
export type Found = { users: UserList; leases: LeaseList }

const line = (parts: (string | null | undefined)[]) => parts.filter((p) => p).join(' · ')

function personHit(world: World, me: Pick<User, 'id' | 'role'>, u: User, q: string): Hit {
  const label = ownerLabel(u)
  const login = u.name ? u.username : null
  const teams = teamsOf(personRow(u, world.teams))
    .map((t) => t.display_name)
    .join(', ')
  const tier = u.quota_tier?.display_name
  // 줄에 선 것 어디에도 친 글자가 없으면 이메일로 걸린 것이다 — 그 값을 보인다.
  const email = !matchAny([label, login, teams, tier], q) && match(u.email, q) ? u.email : null
  return {
    key: u.id,
    label,
    detail: line([login, email, teams, tier]),
    to: opensDetail(world, me, u.id) ? `/users/${u.id}` : null,
  }
}

export function findAll(world: World, me: Pick<User, 'id' | 'role'>, query: string, found: Found | null): HitGroup[] {
  const q = query.trim().toLowerCase()
  if (q === '') return []
  const nodeName = new Map(world.nodes.map((r) => [r.node.id, r.name]))

  const groups: HitGroup[] = [
    {
      kind: 'user',
      title: '사용자',
      hits: (found?.users.items ?? []).map((u) => personHit(world, me, u, q)),
      total: found?.users.total,
    },
    {
      kind: 'team',
      title: '팀',
      hits: world.teams
        .filter((t) => matchAny([t.display_name, ...(t.tags ?? [])], q))
        .map((t) => ({
          key: t.id,
          label: t.display_name,
          detail: line([`멤버 ${t.members.length}명`, (t.tags ?? []).join(', ')]),
          to: `/teams/${t.id}`,
        })),
    },
    {
      kind: 'vm',
      title: 'VM',
      hits: (found?.leases.items ?? []).map((l) => ({
        key: l.id,
        label: l.vm?.name ?? l.name,
        detail: line([ownerLabel(l.owner), l.vm?.node_id ? nodeName.get(l.vm.node_id) : null]),
        to: `/vms/${l.id}`,
      })),
      total: found?.leases.total,
    },
    {
      kind: 'cluster',
      title: '클러스터',
      hits: world.clusters
        .filter((c) => match(c.name, q))
        .map((c) => ({
          key: c.id,
          label: c.name,
          detail: line([`노드 ${membersOf(world, c.id).length}대`, clusterStatus(c)]),
          to: '/clusters',
          query: c.name,
        })),
    },
    {
      kind: 'node',
      title: '노드',
      hits: world.nodes
        .filter((r) => match(r.name, q))
        .map((r) => ({
          key: r.node.id,
          label: r.name,
          detail: line([world.clusters.find((c) => c.id === r.node.cluster_id)?.name ?? '클러스터 미연결', statusLabel[r.status]]),
          to: `/nodes/${r.node.id}`,
        })),
    },
  ]
  const head = (h: Hit) => (h.label.toLowerCase().startsWith(q) ? 0 : 1)
  return groups
    .map((g) => ({ ...g, hits: [...g.hits].sort((a, b) => head(a) - head(b)) }))
    .filter((g) => g.hits.length > 0)
}
